import { useState } from 'react';
import { ActionIcon, Button, Group, Menu, Popover, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconListSearch, IconSparkles, IconTag } from '@tabler/icons-react';
import type { ArgumentGroup, FlowRow, Round } from '@flow/core';
import { run, useUseCases } from '../../app/services';
import { useAiAvailable, useSuggestTitle } from '../ai/useAi';

const short = (s: string, n = 48) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Toolbar: jump to an argument, and name the one you're in. */
export const ArgumentTools = ({ round, groups, rows, cursorRowId, onJump, onDone }: {
  round: Round; groups: ArgumentGroup[]; rows: FlowRow[]; cursorRowId: string | null; onJump: (rowId: string) => void;
  /** Give keyboard focus back to the flow. */
  onDone: () => void;
}) => {
  const uc = useUseCases();
  const ai = useAiAvailable();
  const suggest = useSuggestTitle();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const titleOf = (rowId: string) => rows.find((r) => r.id === rowId)?.title;
  const current = cursorRowId ? groups.find((g) => g.rowIds.includes(cursorRowId)) : undefined;

  const save = async (title: string) => {
    if (current && (await run(uc.setArgumentTitle(round.id, current.headRowId, title, 'judge')))) {
      setOpen(false);
      onDone();
    }
  };

  return (
    <Group gap={4} wrap="nowrap">
      <Menu position="bottom-end" width={280} disabled={groups.length === 0}>
        <Menu.Target>
          <Tooltip label="Jump to an argument">
            <ActionIcon onMouseDown={(e) => e.preventDefault()} disabled={groups.length === 0} aria-label="Jump to an argument" data-testid="argument-jump">
              <IconListSearch size={16} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Arguments on this sheet</Menu.Label>
          {groups.map((g, i) => {
            const t = titleOf(g.headRowId);
            return (
              <Menu.Item key={g.headRowId} onClick={() => onJump(g.headRowId)}>
                <Text size="sm" fw={t ? 600 : 400} lineClamp={1}>{i + 1}. {t ?? short(g.text.replace(/\s+/g, ' '))}</Text>
                {t && <Text size="xs" c="dimmed" lineClamp={1}>{short(g.text.replace(/\s+/g, ' '), 60)}</Text>}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
      <Popover opened={open} onChange={(o) => { setOpen(o); if (!o) onDone(); }} position="bottom-end" width={300} trapFocus withArrow shadow="md">
        <Popover.Target>
          <Tooltip label={current ? 'Name this argument' : 'Put the cursor in an argument to name it'}>
            <ActionIcon
              onMouseDown={(e) => e.preventDefault()}
              disabled={!current}
              onClick={() => {
                setDraft(current ? titleOf(current.headRowId) ?? '' : '');
                setOpen((o) => !o);
              }}
              aria-label="Name this argument"
              data-testid="argument-name"
            >
              <IconTag size={16} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown>
          {current && (
            <form onSubmit={(e) => { e.preventDefault(); void save(draft); }}>
              <Stack gap="xs">
                <Text size="xs" c="dimmed" lineClamp={2}>{current.text}</Text>
                <TextInput size="xs" placeholder="e.g. Jobs from subsidies" value={draft} onChange={(e) => setDraft(e.currentTarget.value)} maxLength={60} aria-label="Argument title" data-autofocus />
                <Group gap={6} justify="space-between">
                  {ai ? (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      leftSection={<IconSparkles size={12} />}
                      loading={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const t = await suggest(round, current.text).catch(() => '');
                          if (t) setDraft(t);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Suggest
                    </Button>
                  ) : <span />}
                  <Group gap={6}>
                    {titleOf(current.headRowId) && <Button size="compact-xs" variant="default" onClick={() => void save('')}>Clear</Button>}
                    <Button size="compact-xs" variant="filled" type="submit">Save</Button>
                  </Group>
                </Group>
              </Stack>
            </form>
          )}
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};
